import type { WaterSolutionGasRswCalculator } from "../d";
import { TemperatureUnits } from "@/libs/units/temperature";
import {
  Dm12Var,
  Dm1Var,
  Dm2Var,
  Dm32Var,
  EmVar,
  EwoVar,
  Fm12Var,
  Fm1Var,
  Fm32Var,
  FwoVar,
  calculateBrineWaterDensity,
  calculateEb,
  calculateFb,
  calculateMolarSaltConcentration,
  calculatePbwAt70Mpa,
  calculateVbo,
  calculatelbAt70Mpa,
  calculatelbAtStockTank,
  pwAt70Mpa
} from "./helpers";
export const waterSolutionGasRswCalculator: WaterSolutionGasRswCalculator =
  params => {
    const { salinity_ppm, gasCompressibilityFactor } = params;

    const stockTankTemperatureK = TemperatureUnits.Kelvin.toThisUnit(60);
    const stockTankPressurePo = 0.1013529;

    const gasVolumeAtStockTank = calculateGasVolumeAtStockTank(
      gasCompressibilityFactor,
      stockTankTemperatureK,
      stockTankPressurePo
    );

    const methaneBrineSolubility = 0.225703134;
    const molarSaltConcentration =
      calculateMolarSaltConcentration(salinity_ppm);

    const pbwAt70Mpa = calculatePbwAt70Mpa(
      pwAt70Mpa,
      Dm2Var,
      Dm32Var,
      Dm1Var,
      Dm12Var,
      molarSaltConcentration
    );

    const Eb = calculateEb(EwoVar, EmVar, molarSaltConcentration);
    const Fb = calculateFb(
      FwoVar,
      Fm32Var,
      Fm1Var,
      Fm12Var,
      molarSaltConcentration
    );

    const lb70Mpa = calculatelbAt70Mpa(Eb, Fb);
    const lbStockTank = calculatelbAtStockTank(stockTankPressurePo, Eb, Fb);

    const brineWaterDensity = calculateBrineWaterDensity(
      pbwAt70Mpa,
      lbStockTank,
      lb70Mpa
    );

    const Vbo = calculateVbo(brineWaterDensity);
    const waterSolutionGasCM3 = calculateWaterSolutionGasCM3(
      gasVolumeAtStockTank,
      methaneBrineSolubility,
      molarSaltConcentration,
      Vbo
    );

    const waterPhaseGasSolubilitySCFSTB =
      calculateWaterSolutionGasSCFSTB(waterSolutionGasCM3);

    return {
      waterPhaseGasSolubilitySCFSTB,
      waterSolutionGasCM3,
      molarSaltConcentration,
      methaneBrineSolubility,
      gasVolumeAtStockTank
    };
  };

// H44: =(C15*8.314467*H42)/C70
function calculateGasVolumeAtStockTank(
  gasCompressibilityFactor: number,
  stockTankTemperatureK: number,
  stockTankPressurePo: number
): number {
  return (
    (gasCompressibilityFactor * 8.314467 * stockTankTemperatureK) /
    stockTankPressurePo
  );
}

// H46: =(H44*N112)/((1000+N48*58.4428)*C118)
function calculateWaterSolutionGasCM3(
  gasVolumeAtStockTank: number,
  methaneBrineSolubility: number,
  molarSaltConcentration: number,
  Vbo: number
): number {
  return (
    (gasVolumeAtStockTank * methaneBrineSolubility) /
    ((1000 + molarSaltConcentration * 58.4428) * Vbo)
  );
}

// H48: =H46*5.61458333
function calculateWaterSolutionGasSCFSTB(waterSolutionGasCM3: number): number {
  return waterSolutionGasCM3 * 5.61458333;
}
